import { z } from "zod";
import { PaginationSchema } from "@/components/paginate/types";
import {
  AiServicesGraphResponseSchema,
  AggregatedAiServicesGraphResponseSchema,
} from "@/pages/summary/context/schemas";

// ── API params ──

export interface AIServiceDateParams {
  start_date: string;
  end_date: string;
  timezone?: string;
  instance_id?: string;
}

export interface AIServicesSummaryParams extends AIServiceDateParams {
  sort_by?: string;
  sort_order?: string;
}

export type AIServiceGraphParams = AIServiceDateParams & {
  aggregation_level?: string;
} & Record<string, string | string[] | number | undefined>;

export type AIServiceUsageParams = AIServiceDateParams & {
  page?: number;
  size?: number;
  sort_by?: string;
  sort_order?: string;
  cost_filter?: string;
  cost_filter_mode?: string;
} & Record<string, string | string[] | number | undefined>;

export interface AIServiceFilterValuesParams extends AIServiceDateParams {
  column: string;
  prefix?: string;
  page?: number;
  size?: number;
}

// ── Graph component types (derived from shared schemas) ──

export type AiServicesGraphItem = z.infer<typeof AiServicesGraphResponseSchema>;
export type AiServicesGraphResponse = z.infer<typeof AggregatedAiServicesGraphResponseSchema>;

// ── Enums ──

export const AIServiceType = {
  CORTEX_ANALYST: "cortex_analyst",
  CORTEX_DOCUMENT_PROCESSING: "cortex_document_processing",
  CORTEX_FINE_TUNING: "cortex_fine_tuning",
  CORTEX_FUNCTIONS: "cortex_functions",
  CORTEX_SEARCH: "cortex_search",
  CORTEX_CODE_CLI: "cortex_code_cli",
  CORTEX_CODE_SNOWSIGHT: "cortex_code_snowsight",
  CORTEX_AGENT: "cortex_agent",
  SNOWFLAKE_INTELLIGENCE: "snowflake_intelligence",
} as const;

export type AIServiceTypeValue = (typeof AIServiceType)[keyof typeof AIServiceType];

// ── Summary (main page table) ──

export const aiServiceSummaryItemSchema = z.object({
  service_type: z.string(),
  service_name: z.string(),
  total_cost: z.number(),
  total_credits: z.number(),
  record_count: z.number(),
  percentage_of_total: z.number(),
});
export type AIServiceSummaryItem = z.infer<typeof aiServiceSummaryItemSchema>;

export const aiServicesSummaryResponseSchema = z.object({
  services: z.array(aiServiceSummaryItemSchema),
  total_cost: z.number(),
  currency: z.string(),
});
export type AIServicesSummaryResponse = z.infer<typeof aiServicesSummaryResponseSchema>;

// ── Graph (both main + detail pages) ──

export const aiServiceGraphPointSchema = z.object({
  date: z.string(),
  cost: z.number(),
  credits: z.number(),
  record_count: z.number(),
});
export type AIServiceGraphPoint = z.infer<typeof aiServiceGraphPointSchema>;

export const aiServiceGraphResponseSchema = z.object({
  service_type: z.string(),
  service_name: z.string(),
  graph: z.array(aiServiceGraphPointSchema),
  total_cost: z.number(),
  total_credits: z.number(),
  currency: z.string(),
});
export type AIServiceGraphResponse = z.infer<typeof aiServiceGraphResponseSchema>;

// ── Usage detail (detail page table) ──

export const columnDefinitionSchema = z.object({
  key: z.string(),
  label: z.string(),
  sortable: z.boolean(),
  data_type: z.enum(["string", "number", "datetime", "link"]),
  link_type: z.enum(["query", "warehouse"]).nullable().optional(),
  filterable: z.boolean().optional().default(false),
});
export type ColumnDefinition = z.infer<typeof columnDefinitionSchema>;

export const aiServiceUsageResponseSchema = z.object({
  service_type: z.string(),
  data: z.array(z.record(z.unknown())),
  pagination: PaginationSchema,
  columns: z.array(columnDefinitionSchema),
});
export type AIServiceUsageResponse = z.infer<typeof aiServiceUsageResponseSchema>;

// ── Breakdown (dimension breakdown for detail pages) ──

export interface AIServiceBreakdownParams extends AIServiceDateParams {
  aggregation_level?: string;
  dimension?: string;
}

export const aiServiceBreakdownPointSchema = z.object({
  date: z.string(),
  dimension_value: z.string(),
  cost: z.number(),
  credits: z.number(),
});

export const aiServiceBreakdownResponseSchema = z.object({
  service_type: z.string(),
  dimension: z.string(),
  available_dimensions: z.array(z.string()),
  data: z.array(aiServiceBreakdownPointSchema),
  pivoted_data: z.array(z.record(z.unknown())).default([]),
  dimension_keys: z.array(z.string()).default([]),
  currency: z.string(),
});
export type AIServiceBreakdownResponse = z.infer<typeof aiServiceBreakdownResponseSchema>;

// ── Filter values (distinct column values for dropdown filters) ──

export const aiServiceFilterValuesResponseSchema = z.object({
  column: z.string(),
  values: z.array(z.string()),
  total: z.number(),
});
export type AIServiceFilterValuesResponse = z.infer<typeof aiServiceFilterValuesResponseSchema>;
